import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { BusyService } from '../../_services/busy.service';
import { GlobalService } from '../../_services/global.service';
import { ItineraryService, itineraryrow } from '../../_services/itinerary.service';
import { DaysheetOrderService } from '../../_services/daysheet-order.service';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';
import { tag_returndata } from 'src/types/tag_returndata';
import { tag } from 'src/types/tag';

@Component({
  selector: 'app-itineraryreport',
  templateUrl: './itineraryreport.component.html',
  styleUrls: ['./itineraryreport.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ItineraryreportComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;

  FromDate: Date = new Date();
  ToDate: Date = new Date();

  tags: tag[] = [];
  chosentags: tag[] = [];

  resources: any[] = [];
  chosenresources: any[] = [];
  private resourceOrder = new Map<string, number>();
  //Which resources the sheet shows, from the Daysheet Resource Order page - null until it loads.
  private visible: Map<string, boolean> = null;
  //Kept so the rows can be rebuilt when the Daysheet order lands after them.
  private bookings: any[] = [];
  //Resource to its concurrent uses, shown in brackets on each section heading.
  private resourceCapacity = new Map<string, number>();
  //The resource sections folded shut, by resource id. Kept across a Go, so a section stays shut
  //while the dates are changed.
  collapsed = new Set<string>();

  ReportRows: itineraryrow[] = [];
  //Every line the report found, before the resource filter is applied. Kept so changing the
  //filter re-cuts the sheet without going back to the server.
  AllRows: itineraryrow[] = [];

  guests: number = 0;

  cols: any[] = this.itineraryService.cols;

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService,
    private itineraryService: ItineraryService,
    private daysheetOrder: DaysheetOrderService) { }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if (this.httpResponse.succeeded) {
        this.AccessAllowed = getAccess("REPORTS", this.httpResponse.data);

        this.loadTags();

        //Asked for together. The rows are drawn as soon as the bookings land and are rebuilt
        //when the Daysheet order does, whichever way round that happens.
        this.loadResources();
        this.loadgrid();
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  loadTags() {
    this.http.get(bmxurl + "TagList/" + this.VenueID + "/false").subscribe(response => {
      this.tags = (response as tag_returndata).data;
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Tags.'});
    });
  }

  /*
    Which resources the sheet shows and in what order come from the Daysheet Resource Order page,
    the same as the Daysheet - see ItineraryService.fromDaysheetOrder. If that order cannot be
    had, each resource's own Show in Itinerary switch decides, in the Resources screen's order.
  */
  loadResources() {
    this.daysheetOrder.load(this.VenueID).subscribe(result => {
      if (result.failed) {
        const resources = result.items.filter(item => item.resource != null).map(item => item.resource);
        this.resourceOrder = this.itineraryService.orderOf(resources);
        //Nothing to go by, so everything is shown rather than nothing.
        this.visible = new Map<string, boolean>();
        this.messageService.add({severity:'warn', summary:'Daysheet order', detail:'The Daysheet Resource Order could not be loaded, so the report is showing every resource.'});
      }
      else {
        const layout = this.itineraryService.fromDaysheetOrder(result.items);
        this.resourceOrder = layout.order;
        this.visible = layout.visible;
      }

      this.resources = this.itineraryService.shownResources(result.items, this.visible);
      this.resourceCapacity = this.itineraryService.capacityOf(
        result.items.filter(item => item.resource != null).map(item => item.resource));

      //The bookings may already be drawn - rebuilt so the right resources are on the sheet.
      this.AllRows = this.itineraryService.buildRows(this.bookings, this.resourceOrder, this.visible);
      this.applyResourceFilter();
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Resources.'});
    });
  }

  doneDate() {
    this.loadgrid();
  }

  editdata(bookingId: string) {
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  loadgrid() {
    this.busyService.busy();

    const searchCrit = this.itineraryService.searchCriteria(
      this.VenueID,
      ConvertDateToDB(this.FromDate),
      ConvertDateToDB(this.ToDate),
      this.chosentags);

    this.http.post(bmxurl + "/BookingList", searchCrit).subscribe(response => {
      this.bookings = ((response as booking_returndata).data ?? [])
        .filter((b: any) => b.inProgress == false);

      //Not built until the Daysheet order is in, so a resource switched off there never flashes
      //up first - loadResources builds the rows when it lands.
      this.AllRows = this.visible == null ? [] : this.itineraryService.buildRows(this.bookings, this.resourceOrder, this.visible);

      this.applyResourceFilter();
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error getting bookings', detail:'An error occurred getting Bookings.'});
    });
  }

  /*
    Deliberately does nothing. A PrimeNG table that groups its rows sorts them itself by the
    grouping field - here the resource id, a GUID - which throws away the order the service put
    them in and draws the sections in GUID order instead. Marking the table customSort hands
    that sort to this method, and leaving the rows alone is the sort that is wanted: they
    arrive already in the Daysheet order.
  */
  keepOrder() { }

  applyResourceFilter() {
    const arranged = this.itineraryService.arrange(this.AllRows, this.chosenresources, this.resourceOrder);

    this.ReportRows = arranged.rows;
    this.guests = arranged.guests;
  }

  //A section heading's name with the resource's concurrent uses in brackets - just the name for
  //a resource the resource list does not know.
  sectionTitle(row: itineraryrow): string {
    const capacity = this.itineraryService.capacityFor(this.resourceCapacity, row);

    return row.resourceName + (capacity == null ? "" : " (" + capacity + ")");
  }

  toggleSection(row: itineraryrow) {
    if (this.collapsed.has(row.resourceId)) {
      this.collapsed.delete(row.resourceId);
    }
    else {
      this.collapsed.add(row.resourceId);
    }
  }

  collapseAll() {
    this.collapsed = new Set((this.ReportRows ?? []).map(row => row.resourceId));
  }

  expandAll() {
    this.collapsed = new Set<string>();
  }

  /*
    Every row the report is showing, folded sections included, with the resource's capacity
    alongside its name. Done here rather than by the table's own export so the capacity can go
    in and the file gets a proper name rather than download.csv.
  */
  exportCsv() {
    const cols = [...this.cols];
    cols.splice(cols.findIndex(col => col.field == 'resourceName') + 1, 0, { field: 'capacity', header: 'Capacity' });

    const valueOf = (row: itineraryrow, field: string): string => {
      if (field == 'capacity') {
        const capacity = this.itineraryService.capacityFor(this.resourceCapacity, row);
        return capacity == null ? "" : String(capacity);
      }

      return String((row as any)[field] ?? "");
    };

    const cell = (value: string): string => '"' + value.replace(/"/g, '""') + '"';
    const lines = [cols.map(col => cell(col.header)).join(",")]
      .concat((this.ReportRows ?? []).map(row => cols.map(col => cell(valueOf(row, col.field))).join(",")));

    //The byte order mark is what makes Excel read the pound sign and accents correctly.
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = "ItineraryReport_" + this.fileDate(this.FromDate) + "_to_" + this.fileDate(this.ToDate) + ".csv";
    link.click();

    URL.revokeObjectURL(link.href);
  }

  //The picker's own day, read locally, so the file is named for the dates on screen.
  private fileDate(value: Date): string {
    const date = new Date(value);
    const pad = (n: number) => String(n).padStart(2, "0");

    return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate());
  }

  //The period cannot run backwards. Moving the From date past the To date takes the To date with
  //it, and the To picker will not open on anything earlier than the From date.
  fromDateChanged() {
    if (this.FromDate == null || this.ToDate == null) {
      return;
    }

    if (new Date(this.ToDate) < new Date(this.FromDate)) {
      this.ToDate = new Date(this.FromDate);
    }
  }
}
